import { TriangleAlert } from 'lucide-react'
import Tooltip from 'rc-tooltip'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useCalendars } from 'src/modules/calendar/hooks/use-calendars'
import { Modal, SelectField, Typography } from 'src/modules/core/components'
import { useDisplayTimezone } from 'src/modules/core/context'
import { CalendarDto } from 'src/types/calendar.dto'
import classes from './timezone-selector.module.scss'

const TIMEZONE_MISMATCH_TOOLTIP = 'One or more of your calendars is in a different time zone. Click to learn more.'

function getCalendarTimezone(calendar: CalendarDto): string {
  const custom = calendar.timezone_custom?.trim()
  return custom ? custom : calendar.timezone
}

export function TimezoneSelector() {
  const [modalOpen, setModalOpen] = useState(false)
  const { timezone, setTimezone, displayTimezoneOptions, displayTimezoneOffsetString, hasExplicitDisplayTimezone } =
    useDisplayTimezone()
  const calendars = useCalendars({}, { sync: false })

  const hasTimezoneMismatch = useMemo(() => {
    const data = calendars.data ?? []
    const selected = data.filter((c) => c.selected)
    if (selected.length === 0) return false
    return selected.some((c) => getCalendarTimezone(c) !== timezone)
  }, [calendars.data, timezone])

  return (
    <>
      <div className={classes.row}>
        <button type="button" className={classes.button} onClick={() => setModalOpen(true)}>
          Times shown in {displayTimezoneOffsetString}
        </button>
        {hasTimezoneMismatch && (
          <Tooltip
            placement="bottom"
            mouseEnterDelay={0.1}
            overlay={<span>{TIMEZONE_MISMATCH_TOOLTIP}</span>}
            overlayClassName={classes.tooltipOverlay}
          >
            <button
              type="button"
              className={classes.warningIcon}
              onClick={() => setModalOpen(true)}
              aria-label={TIMEZONE_MISMATCH_TOOLTIP}
            >
              <TriangleAlert />
            </button>
          </Tooltip>
        )}
      </div>
      {modalOpen && (
        <Modal
          onClose={() => setModalOpen(false)}
          title={
            <Typography variant="h3" className={classes.modalTitle}>
              Display time zone
            </Typography>
          }
        >
          <SelectField
            value={displayTimezoneOptions.find((tz) => tz.value === timezone)}
            onChange={(option: { value?: string }) =>
              setTimezone(option?.value ?? displayTimezoneOptions[0]?.value ?? 'UTC')
            }
            options={displayTimezoneOptions}
            closeMenuOnSelect={true}
            isSearchable={true}
          />
          {hasExplicitDisplayTimezone && (
            <button
              type="button"
              className={classes.useLocationLink}
              onClick={() => setTimezone(displayTimezoneOptions[0]?.value ?? 'UTC')}
            >
              Use my current location
            </button>
          )}
          <p className={classes.helperText}>
            This setting only affects how times are displayed. It does not change when reminders are sent or the times
            included in reminder messages.
          </p>
          {hasTimezoneMismatch && (
            <div className={classes.mismatchAlert} role="alert">
              <TriangleAlert className={classes.mismatchAlertIcon} aria-hidden />
              <span>
                Your calendars use different time zones. Reminders are sent according to each calendar&apos;s time zone
                and may not match your selected display time zone.{' '}
                <Link
                  to="/dashboard/settings?tab=calendars"
                  className={classes.mismatchAlertLink}
                  onClick={() => setModalOpen(false)}
                >
                  Review the time zones for your calendars.
                </Link>
              </span>
            </div>
          )}
        </Modal>
      )}
    </>
  )
}
